import React, { forwardRef, useMemo, useState } from "react";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";
import classNames from "classnames";
import PropTypes from "prop-types";

import Typography from "../Typography/Typography";
import IconView from "../IconView/IconView";

import { escapeRegExp } from "../../utils/string/string";
import { handleKeyDownSelectItem } from "../../utils/keyboard-navigation/keyboard-navigation";
import Ripple from "@/shared/components/Button/Ripple";
import { radioButtonLabelClassName } from "./RadioButton.constants";

import styles from "./RadioButton.module.scss";

const RadioButton = forwardRef(({
  className,
  value, onSelect,
  isTranslateLabel,
  groupName, isInline,
  disabled, selected, style, icon, label, hideRadio,
  searchValue,
  onKeyDown,
}, ref) => {
  const { t } = useSharedTranslation();

  const [isFocused, setIsFocused] = useState(false);

  const labelTranslated = isTranslateLabel ? t(label) : label;
  const labelFinal = useMemo(() => {
    return searchValue ? labelTranslated.replace(
      new RegExp(escapeRegExp(searchValue, "gi")),
      (strMatch) => "<mark>" + strMatch + "</mark>",
    ) : labelTranslated;
  }, [searchValue, labelTranslated]);

  const handleKeyDown = (event, id) => {
    if (onSelect) {
      handleKeyDownSelectItem(event, (e) => onSelect(id, e));
    }
  };

  return (
    <label
      ref={ref}
      className={classNames(className, "radio-item", styles["radio-item"],
        (selected ? styles["active"] : styles["inactive"]), {
          [styles["disabled"]]: disabled,
          [styles["is-inline"]]: isInline,
          [styles["focused"]]: isFocused,
        })}
      onKeyDown={onKeyDown}
    >
      <input
        type="radio"
        name={groupName}
        className={styles["input"]}
        // NOTE:
        // Don't use attribute "checked" to indicate whether the checkbox is checked or not.
        // In HTML attribute "checked" is an analogue of "defaultValue".
        // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#attr-checked
        aria-checked={selected}
        onChange={() => {}}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onClick={(e) => onSelect && onSelect(value, e)}
        onKeyDown={(event) => handleKeyDown(event, value)}
      />
      {!hideRadio && (
        icon ? <><IconView icon={icon} />&nbsp;</> : <span className={styles["wrap-check"]} />
      )}
      {label && <Typography
        tag="span"
        variant="body3"
        color="inherit"
        className={classNames(radioButtonLabelClassName, styles["label"])}
        style={style}
        dangerouslySetInnerHTML={{ __html: labelFinal }}
      />}

      <Ripple />
    </label>
  );
});

RadioButton.propTypes = {
  style: PropTypes.object,
  icon: PropTypes.any,
  label: PropTypes.string,
  isTranslateLabel: PropTypes.bool,
  isInline: PropTypes.bool,
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  onSelect: PropTypes.func,
  groupName: PropTypes.string
};

export default RadioButton;
